import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';

import styles from './Toast.module.scss';
import Card from '../Card';
import Icon from '../_experimental_/Icon';
import Text from '../Text';
import IconButton from '../IconButton';
import Portal from '../Portal';
import isTestRender from '../utils/isTestRender';

export type Position =
  | 'topLeft'
  | 'topCenter'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight';

export type ToastStatus =
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  | 'attention';

export interface Props {
  children?: React.ReactNode;
  /**  Custom className to override or add styles to the toast message. */
  className?: Parameters<typeof cx>[0];
  /** The status of the toast message. */
  status?: ToastStatus;
  /** The heading text of the toast message. */
  headingText?: string;
  /** Whether the toast message is open or not. */
  isOpen: boolean;
  /** The ref of the parent node. Used to attach the Toast to something other than the document body. */
  parentNodeRef?: React.RefObject<HTMLElement>;
  /** The position of the toast message. */
  position?: Position;
  /** The duration of the toast message in milliseconds. */
  durationMs?: number;
  /** Callback fired when the escape key is pressed. */
  onEscape?: () => void;
  /** Callback fired when the toast message is closed. */
  onClose: () => void;
}

const ToastHeading: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className={styles.heading}>
    {icon}
    <Text
      weight='semiBold'
      font='sans'
      as='span'
      className={styles.headingText}
    >
      {children}
    </Text>
  </div>
);

const iconForStatus = (status: Props['status']) => {
  switch (status) {
    case 'success':
      return 'checkmark';
    case 'warning':
      return 'alert-warning';
    case 'error':
      return 'alert-error';
    case 'attention':
    case 'default':
    default:
      return 'information';
  }
};

enum TriggerState {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  UNINTERACTED = 'uninteracted',
}

export const Toast: React.FC<Props> = ({
  children,
  className,
  headingText = 'Notification',
  status = 'default',
  isOpen,
  onClose,
  parentNodeRef,
  position = 'topCenter',
  durationMs = 5000,
  onEscape,
}) => {
  const getCurrentTimestamp = () => new Date().valueOf();

  const [triggerState, setTriggerState] = useState<TriggerState>(
    TriggerState.INACTIVE,
  );
  const [hoverStartTime, setHoverStartTime] = useState<number | null>(null);
  const [hoverEndTime, setHoverEndTime] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState<boolean>(false);

  const toastRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<HTMLDivElement>(null);

  let startTime = useRef<number>(getCurrentTimestamp());
  let now = useRef<number>(getCurrentTimestamp());
  let remainingTime = useRef<number>(durationMs);
  let totalHoverTime = useRef<number>(0);

  const closeToast = useCallback(() => {
    onClose();
    setIsClosing(true);
  }, [onClose]);

  const escapeKeyHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape && onEscape();
        closeToast();
      }
    },
    [onEscape, closeToast],
  );

  const onToastMouseEnter = useCallback(() => {
    setTriggerState(TriggerState.ACTIVE);
    setHoverStartTime(getCurrentTimestamp());
    cancelAnimationFrame(requestId.current);
  }, []);

  const onToastMouseLeave = useCallback(() => {
    setTriggerState(TriggerState.INACTIVE);
    setHoverEndTime(getCurrentTimestamp());
    if (hoverStartTime !== null) {
      const hoverDuration = getCurrentTimestamp() - hoverStartTime;
      totalHoverTime.current += hoverDuration;
      if (totalHoverTime.current > durationMs) {
        remainingTime.current = getCurrentTimestamp() - startTime.current;
      }
    }
    cancelAnimationFrame(requestId.current);
  }, [hoverStartTime, durationMs]);

  useLayoutEffect(() => {
    if (!toastRef.current) return;

    const toastElement = toastRef.current;
    toastElement.addEventListener('mouseenter', onToastMouseEnter);
    toastElement.addEventListener('mouseleave', onToastMouseLeave);

    document.addEventListener('keyup', escapeKeyHandler);

    return () => {
      toastElement.removeEventListener('mouseenter', onToastMouseEnter);
      toastElement.removeEventListener('mouseleave', onToastMouseLeave);

      document.removeEventListener('keyup', escapeKeyHandler);
    };
  }, [escapeKeyHandler, onToastMouseEnter, onToastMouseLeave, parentNodeRef]);

  const requestId = useRef<number>(0);

  useEffect(() => {
    const shouldAnimate =
      triggerState !== TriggerState.ACTIVE &&
      isOpen &&
      timerRef.current &&
      !isTestRender() &&
      remainingTime.current > 0;

    const play = () => {
      const currentTimerElement = timerRef.current;
      if (currentTimerElement) {
        currentTimerElement.style.width = `calc(${remainingTime.current /
          durationMs} * 100%)`;
      }
      requestId.current = requestAnimationFrame(animateTimer);
    };

    const pause = () => {
      cancelAnimationFrame(requestId.current);
    };

    const setRemainingTime = () => {
      now.current = getCurrentTimestamp();
      if (hoverStartTime !== null && hoverEndTime !== null) {
        remainingTime.current =
          durationMs -
          (now.current - startTime.current) +
          totalHoverTime.current;
      } else {
        remainingTime.current = durationMs - (now.current - startTime.current);
      }
    };

    const animateTimer = () => {
      if (remainingTime.current <= 0) {
        closeToast();
        return;
      }
      if (shouldAnimate) {
        setRemainingTime();
        play();
      } else {
        pause();
      }
    };

    animateTimer();

    return () => {
      cancelAnimationFrame(requestId.current);
    };
  }, [
    isOpen,
    durationMs,
    closeToast,
    hoverStartTime,
    hoverEndTime,
    triggerState,
  ]);

  useEffect(() => {
    setTriggerState(TriggerState.INACTIVE);
    setHoverStartTime(null);
    setHoverEndTime(null);
    totalHoverTime.current = 0;
    startTime.current = getCurrentTimestamp();
    now.current = getCurrentTimestamp();
    remainingTime.current = durationMs;
  }, [durationMs, isOpen]);

  return (
    <Portal
      {...(parentNodeRef?.current != null && {
        mountedNode: parentNodeRef?.current,
      })}
      className={cx(
        styles.portal,
        styles[position],
        parentNodeRef && styles.customOutlet,
      )}
    >
      <div
        className={cx(
          styles.toast,
          styles[status],
          isOpen && styles.showToast,
          isClosing && styles.hideToast,
          isTestRender() && styles.testRender,
        )}
        ref={toastRef}
        aria-live='assertive'
        role='alert'
      >
        <Card
          headerLeadingContentRows={
            <ToastHeading
              icon={
                <Icon
                  appearance={iconForStatus(status)}
                  size={18}
                  className={styles.icon}
                />
              }
            >
              {headingText}
            </ToastHeading>
          }
          bodyClassName={styles.body}
          body={children}
          className={cx(styles.card, className)}
          headerTrailingObject={
            <IconButton
              accessibilityLabel='Close'
              icon={<Icon appearance='close' />}
              onClick={() => {
                closeToast();
              }}
            />
          }
        />
        <div className={styles.timer} ref={timerRef} />
      </div>
    </Portal>
  );
};

Toast.displayName = 'Toast';
export default Toast;
export { styles };
